import { useEffect } from "react";
import { isRouteErrorResponse, useParams, useRouteError } from "react-router-dom";
import { captureException } from "@sentry/react";
import { getLocaleDirection } from "@/lib/i18n/portal";
import { SellerIntlProvider } from "@/lib/i18n/intl";
import { getResolvedSellerLocale } from "@/app/locale";
import { RouteErrorFallback } from "./RouteErrorFallback";

/** Readable one-liner for the DEV-only `<details>`; never shown to a seller. */
function describeError(error: unknown): string {
  if (isRouteErrorResponse(error)) {
    return `${error.status} ${error.statusText}\n${JSON.stringify(error.data, null, 2)}`;
  }
  if (error instanceof Error) {
    return error.stack ?? `${error.name}: ${error.message}`;
  }
  return String(error);
}

/**
 * Router-level `errorElement`. Catches what `RouteErrorBoundary` cannot: that
 * boundary lives *inside* `ProtectedLayout` (around `<Outlet/>`), so a throw in
 * the layout itself happens ABOVE it and used to fall through to React Router's
 * built-in screen — a white page with a stack trace and a "Hey developer 👋"
 * note, in front of a seller.
 *
 * Self-contained on purpose. Mounted as a route's `errorElement`, it REPLACES
 * that route's element, so when it stands in for `SellerLocaleLayout` there is
 * no intl provider and no `dir` wrapper left in the tree — it brings its own.
 * (Nested under a surviving layout those simply repeat harmlessly.)
 */
export function RouteErrorElement() {
  const error = useRouteError();
  const { locale: localeParam, storeSlug } = useParams();
  const locale = getResolvedSellerLocale(localeParam);

  // The router swallows the throw, so nothing above `RouterProvider` ever sees
  // it and the Sentry boundary in main.tsx cannot report it for us.
  useEffect(() => {
    captureException(error);
  }, [error]);

  // Without a store in the URL there is no dashboard to return to; `/:locale`
  // resolves to the root redirect, which routes to login or the seller's store.
  const homeHref = storeSlug ? `/${locale}/${storeSlug}` : `/${locale}`;

  return (
    <SellerIntlProvider locale={locale}>
      <div
        className="seller-app"
        lang={locale}
        dir={getLocaleDirection(locale)}
      >
        <RouteErrorFallback
          fullScreen
          // A reload, not a boundary reset: the router keeps the error latched
          // for this match, so re-rendering in place would land right back here.
          onRetry={() => window.location.reload()}
          homeHref={homeHref}
          debugDetail={import.meta.env.DEV ? describeError(error) : undefined}
        />
      </div>
    </SellerIntlProvider>
  );
}
